import cx from 'classnames';
import { t } from 'i18next';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Menu, MenuItem, Sidebar, SubMenu } from 'react-pro-sidebar';
import { useLocation, useMatches } from 'react-router';
import { commonRoutes } from '../../../gameConfig/commonRoutes';
import { Link, TrackedLink } from '../../components/link';
import { EMenuEntry, TMenuEntry } from '../../components/menu/menuHelper';
import { SiteSwitcherBar } from '../../components/siteSwitcherBar';
import { navigateTo, navigateToWithPageRefresh } from '../../libs/history';
import { asset } from '../../services/assetResolverService';
import { useFeatures, useSiteConfig, useSiteMeta } from '../selectors/global';
import WowIcon from '../wow/components/WowIcon';
import { getMobileSiteNavMenuStyles } from './mobileSiteNavMenuStyles';

export type TMobileSiteNavMenuGameSpecificProps = {
  getMenuItems: Function;
  QueueButton: any;
  setMenuToggled: Function;
  isMenuToggled: boolean;
};

type TMobileMenuOptions = {
  prefix: string;
  menus: TMenuEntry[];
  setMenuToggled: Function;
  styles: any;
};

function getLogoPath() {
  if (RIO_SITE.game === 'wowclassic') {
    return '/images/brand/Logo_2ColorWhite_Classic.png';
  } else if (RIO_SITE.game === 'wowera') {
    return '/images/brand/Logo_2ColorWhite_Classic.png';
  } else {
    return '/images/brand/Logo_2ColorWhite.svg';
  }
}

function buildMobileMenus(options: TMobileMenuOptions) {
  const { prefix, menus, setMenuToggled, styles } = options;
  const items: any[] = [];

  menus.forEach((menu: TMenuEntry, index: number) => {
    if (menu.type === EMenuEntry.DIVIDER) {
      // items.push(<Divider key={index} />);
    } else if (menu.type === EMenuEntry.ITEM) {
      let titleData = menu.title;
      if (typeof menu.title === 'string') {
        titleData = t(menu.title);
      } else if (typeof menu.title === 'function') {
        titleData = menu.title(menu);
      }

      const titleWithSuffix = (
        <>
          <span className={menu.titleClass}>{titleData}</span>
          {menu.postIconClass && <i className={menu.postIconClass} />}
          {menu.isNew && <span className="slds-m-left--x-small new-feature-indicator">NEW</span>}
          {menu.isBeta && <span className="slds-m-left--x-small new-feature-indicator">BETA</span>}
          {menu.isLive && <span className="slds-m-left--x-small rwf-live-indicator">LIVE</span>}
        </>
      );

      let titleLine: any = titleWithSuffix;

      if (menu.customIconComponent) {
        titleLine = (
          <>
            <span className="slds-avatar slds-avatar--x-small rio-border--light rio-shadow--small slds-m-right--x-small">
              {menu.customIconComponent}
            </span>

            {titleWithSuffix}
          </>
        );
      } else if (menu.customIcon) {
        titleLine = (
          <>
            <span className="slds-avatar slds-avatar--x-small rio-border--light rio-shadow--small slds-m-right--x-small">
              <img loading="lazy" src={menu.customIcon} />
            </span>

            {titleWithSuffix}
          </>
        );
      } else if (menu.wowIcon) {
        titleLine = (
          <>
            {([] as string[]).concat(menu.wowIcon).map((icon, iconIndex) => {
              return <WowIcon lazy icon={icon} size="xx-small" key={iconIndex} className="slds-m-right--x-small" />;
            })}
            {titleWithSuffix}
          </>
        );
      } else if (menu.iconClassName) {
        titleLine = (
          <>
            <div className={cx(menu.iconClassName, 'slds-m-right--x-small')} />
            {titleWithSuffix}
          </>
        );
      }

      if (menu.children) {
        let childMenus = null;
        if (_.isArray(menu.children) && menu.children.length > 0) {
          childMenus = buildMobileMenus({
            prefix: `${prefix}:child${index}`,
            menus: menu.children,
            setMenuToggled,
            styles
          });
        } else if (typeof menu.children === 'function') {
          childMenus = buildMobileMenus({
            prefix: `${prefix}:child${index}`,
            menus: menu.children(menu),
            setMenuToggled,
            styles
          });
        }

        items.push(
          <SubMenu
            key={`${prefix}:${index}`}
            label={
              <span className={menu.className}>
                {menu.PreIcon && <menu.PreIcon />}
                {titleLine}
                {menu.PostIcon && <menu.PostIcon />}
              </span>
            }
            {...([] as any)}
          >
            {childMenus}
          </SubMenu>
        );
      } else {
        items.push(
          <MenuItem
            key={menu.link}
            component={
              menu.link && menu.link.startsWith('http')
                ? <TrackedLink href={menu.link} onClick={() => setMenuToggled(false)} style={styles.link.style} />
                : <Link to={menu.link || ''} onClick={() => setMenuToggled(false)} style={styles.link.style} />
            }
          >
            <span className={menu.className}>{titleLine}</span>
          </MenuItem>
        );
      }
    }
  });
  return items;
}

export default function MobileSiteNavMenu(props: TMobileSiteNavMenuGameSpecificProps) {
  // react-pro-sidebar uses window.matchMedia which doesn't exist during SSR.
  // Render nothing on server and first client render to avoid hydration mismatch,
  // then mount after useEffect. The sidebar starts hidden (toggled=false) so no flash.
  const [isClient, setIsClient] = useState(false);
  useEffect(() => setIsClient(true), []);

  const currentFeatures = useFeatures();
  const siteMeta = useSiteMeta();
  const siteConfig = useSiteConfig();
  const matches = useMatches();
  const match = _.last(matches);
  const location = useLocation();

  const menuItems = [
    ...props.getMenuItems({
      ...props,
      ...siteMeta,
      ...siteConfig,
      match,
      siteConfig,
      currentFeatures
    }),
    ...(!currentFeatures.premium
      ? [
          {
            type: EMenuEntry.ITEM,
            title: t('nav:main.go-premium'),
            className: 'text-bold text-premium-special text-premium-special--animated',
            link: '/premium'
          }
        ]
      : [])
  ];

  const styles = getMobileSiteNavMenuStyles();

  if (!isClient) {
    return null;
  }

  return (
    <>
      {/* Custom backdrop overlay */}
      {props.isMenuToggled && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100,
            cursor: 'pointer'
          }}
          onClick={() => props.setMenuToggled(false)}
        />
      )}

      <Sidebar
        breakPoint="lg"
        toggled={props.isMenuToggled}
        backgroundColor={styles.sidebar.backgroundColor}
        rootStyles={styles.sidebar.rootStyles}
      >
        <div
          className="slds-text-align--center auth-bar--logo slds-m-vertical--xx-small"
          style={{ position: 'relative' }}
        >
          <div
            onClick={(ev: any) => {
              ev.preventDefault();
              props.setMenuToggled(!props.isMenuToggled);
            }}
            style={{
              position: 'absolute',
              left: '0.5rem',
              top: '50%',
              transform: 'translate(50%, -50%)',
              fontSize: '1.25rem',
              color: '#fff',
              overflow: 'visible'
            }}
          >
            <i className="fa fa-arrow-to-left" />
          </div>

          <Link
            to="/"
            className="auth-bar--logo"
            onClick={() => {
              props.setMenuToggled(!props.isMenuToggled);
            }}
          >
            <img src={asset(getLogoPath())} alt="RaiderIO Logo" />
          </Link>
        </div>
        <div className="sidebar-content">
          <SiteSwitcherBar game={RIO_SITE.game as any} className="slds-grid--align-center slds-m-vertical--xx-small" />
          <Menu style={styles.menu.style} menuItemStyles={styles.menu.menuItemStyles}>
            {buildMobileMenus({
              prefix: 'menus',
              menus: menuItems,
              setMenuToggled: props.setMenuToggled,
              styles
            })}

            <MenuItem>
              <props.QueueButton
                onClick={() => props.setMenuToggled(false)}
                component={
                  <span className="text-bold">
                    {t('nav:mobile.add-to-queue')}{' '}
                    <i className="fas fa-plus slds-m-left--xx-small slds-text-body--small text-raiderio" />
                  </span>
                }
              />
            </MenuItem>
            {currentFeatures.authenticated && (
              <MenuItem className="slds-m-top--large" onClick={() => navigateToWithPageRefresh('/logout')}>
                <span className="text-bold">{t('nav:auth.logout')}</span>
              </MenuItem>
            )}
            {!currentFeatures.authenticated && (
              <li className="ps-menuitem-root slds-m-top--large">
                <div className="slds-col rio-auth-nav slds-text-align--center">
                  <Link
                    className="slds-m-horizontal--x-small rio-auth-nav__button rio-auth-nav__button--brand text-white text-bold"
                    to="/register"
                  >
                    {t('nav:auth.signup')}
                  </Link>

                  <Link
                    className="slds-m-horizontal--x-small rio-auth-nav__button rio-auth-nav__button--hollow text-white text-bold"
                    to={`${commonRoutes.Login}${location.search}`}
                  >
                    {t('nav:auth.login')}
                  </Link>
                </div>
              </li>
            )}
          </Menu>
        </div>
        <div className="sidebar-footer">
          <div className="slds-p-around--small slds-text-align--center slds-m-bottom--medium">
            {[
              ['discord', 'https://discord.gg/raider', t('common:socials.interaction-prompt.discord')],
              ['facebook', 'https://www.facebook.com/raider.io', t('common:socials.interaction-prompt.facebook')],
              ['instagram', 'https://www.instagram.com/raiderio_wow', t('common:socials.interaction-prompt.instagram')],
              ['twitter', 'https://twitter.com/RaiderIO_Wow', t('common:socials.interaction-prompt.twitter')]
            ].map(([type, url, title]) => {
              return (
                <span
                  key={url}
                  className={cx('slds-avatar slds-avatar--small rio-social slds-m-horizontal--small text-white', type)}
                  title={title}
                  onClick={navigateTo.bind(null, url)}
                />
              );
            })}
          </div>
        </div>
      </Sidebar>
    </>
  );
}
